import { useEffect, useMemo, useState } from "react";
import { paymentApi } from "../services/paymentApi";
import { invoiceApi } from "../services/invoiceApi";
import PaymentFormModal from "../components/payments/PaymentFormModal";

const money = (value) => Number(Number(value || 0).toFixed(2));

const statusBadge = (status) => {
  const base =
    "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold capitalize";
  if (status === "completed") {
    return `${base} bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300`;
  }
  if (status === "pending") {
    return `${base} bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300`;
  }
  if (status === "failed") {
    return `${base} bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300`;
  }
  if (status === "refunded") {
    return `${base} bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300`;
  }
  return `${base} bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

function StatCard({ title, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-sm text-slate-500 dark:text-slate-400">{title}</p>
      <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">
        {value}
      </p>
    </div>
  );
}

export default function Payments() {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [dashboard, setDashboard] = useState({
    summary: {
      totalReceived: 0,
      pendingAmount: 0,
      failedCount: 0,
      refundCount: 0,
    },
    breakdown: [],
    recentActivity: [],
  });
  const [invoices, setInvoices] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");

  const [formOpen, setFormOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  const [message, setMessage] = useState("");

  const loadInvoices = async () => {
    const res = await invoiceApi.list("");
    setInvoices(res.data?.invoices || []);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (methodFilter !== "all") params.set("method", methodFilter);
      if (monthFilter !== "all") params.set("month", monthFilter);

      const [paymentsRes, dashboardRes] = await Promise.all([
        paymentApi.list(params.toString() ? `?${params.toString()}` : ""),
        paymentApi.dashboard(),
      ]);

      setPayments(paymentsRes.data?.payments || []);
      setDashboard(
        dashboardRes.data || {
          summary: {
            totalReceived: 0,
            pendingAmount: 0,
            failedCount: 0,
            refundCount: 0,
          },
          breakdown: [],
          recentActivity: [],
        }
      );
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      loadData();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, methodFilter, monthFilter]);

  const allSelectedInfo = useMemo(() => payments.length, [payments.length]);

  const openCreate = () => {
    setEditingPayment(null);
    setFormOpen(true);
  };

  const openEdit = async (id) => {
    try {
      const res = await paymentApi.getById(id);
      setEditingPayment(res.data?.payment || null);
      setFormOpen(true);
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to load payment");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this payment?")) return;

    try {
      await paymentApi.remove(id);
      setMessage("Payment deleted successfully");
      loadData();
      loadInvoices();
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to delete payment");
    }
  };

  const shell =
    "rounded-3xl border border-slate-200 bg-white shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900";
  const input =
    "w-72 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";
  const select =
    "rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100";
  const panel =
    "rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900";

  return (
    <div className="p-6 text-slate-900 dark:text-slate-100">
      <PaymentFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={(msg) => {
          setMessage(msg || "Payment saved successfully");
          loadData();
          loadInvoices();
        }}
        payment={editingPayment}
        invoices={invoices}
      />

      {message ? (
        <div className="mb-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
          {message}
        </div>
      ) : null}

      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-slate-100">
            Payments
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Manage incoming payments, statuses, and transaction history.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search payments..."
            className={input}
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={select}
          >
            <option value="all">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>

          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className={select}
          >
            <option value="all">All Methods</option>
            <option value="bank_transfer">Bank Transfer</option>
            <option value="card">Card</option>
            <option value="cash">Cash</option>
            <option value="online">Online</option>
            <option value="other">Other</option>
          </select>

          <input
            type="month"
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value || "all")}
            className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
          />

          <button
            onClick={openCreate}
            className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-blue-700"
          >
            + New Payment
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          title="Total Received"
          value={`£${money(dashboard.summary.totalReceived).toFixed(2)}`}
        />
        <StatCard
          title="Pending Payments"
          value={`£${money(dashboard.summary.pendingAmount).toFixed(2)}`}
        />
        <StatCard title="Failed Payments" value={dashboard.summary.failedCount} />
        <StatCard title="Refunds" value={dashboard.summary.refundCount} />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[1fr_360px]">
        <div className={shell}>
          <div className="flex items-center justify-between border-b border-slate-100 px-5 pb-4 pt-5 dark:border-slate-800">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Payment Transactions
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Track all payment activity in one place
              </p>
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Showing {allSelectedInfo} payments
            </div>
          </div>

          <div className="mt-4 overflow-x-auto px-5 pb-5">
            <table className="min-w-full">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                <tr>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Ref</th>
                  <th className="px-4 py-3">Vendor</th>
                  <th className="px-4 py-3">Invoice</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {loading ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                    >
                      Loading payments...
                    </td>
                  </tr>
                ) : payments.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="px-4 py-10 text-center text-sm text-slate-500 dark:text-slate-400"
                    >
                      No payments found.
                    </td>
                  </tr>
                ) : (
                  payments.map((p) => (
                    <tr
                      key={p.id}
                      className="border-t border-slate-100 bg-white transition hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:hover:bg-slate-800/60"
                    >
                      <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                        {formatDate(p.paymentDate || p.createdAt)}
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-slate-800 dark:text-slate-100">
                        {p.reference}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                        {p.vendor?.name || p.invoice?.vendor?.name || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                        {p.invoice?.invoiceNumber || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                        {p.paymentMethod?.replace("_", " ") || "-"}
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-slate-800 dark:text-slate-100">
                        £{money(p.amount).toFixed(2)}
                      </td>
                      <td className="px-4 py-4">
                        <span className={statusBadge(p.status)}>{p.status}</span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEdit(p.id)}
                            className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(p.id)}
                            className="rounded-xl border border-red-200 px-3 py-2 text-sm text-red-600 transition hover:bg-red-50 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-950/40"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-6">
          <div className={panel}>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Payment Breakdown
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              By payment method
            </p>

            <div className="mt-6 space-y-5">
              {dashboard.breakdown.map((item) => (
                <div key={item.method}>
                  <div className="mb-2 flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700 dark:text-slate-300">
                      {item.method.replace("_", " ")}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {item.percentage}%
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800">
                    <div
                      className="h-2 rounded-full bg-blue-600"
                      style={{ width: `${item.percentage}%` }}
                    />
                  </div>
                  <div className="mt-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                    £{money(item.amount).toFixed(2)}
                  </div>
                </div>
              ))}
              {dashboard.breakdown.length === 0 ? (
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  No payment data yet.
                </div>
              ) : null}
            </div>
          </div>

          <div className={panel}>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Recent Activity
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Latest payment events
            </p>

            <div className="mt-4 space-y-3">
              {dashboard.recentActivity.map((item) => (
                <div
                  key={item.id}
                  className="rounded-2xl bg-slate-50 p-4 transition-colors duration-300 dark:bg-slate-950"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-slate-800 dark:text-slate-100">
                        {item.title}
                      </div>
                      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        {item.subtitle}
                      </div>
                    </div>
                    <div className="text-xs text-slate-400 dark:text-slate-500">
                      {formatDate(item.timeAgo)}
                    </div>
                  </div>
                </div>
              ))}
              {dashboard.recentActivity.length === 0 ? (
                <div className="text-sm text-slate-500 dark:text-slate-400">
                  No recent activity.
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}